import { inject, injectable } from 'inversify';
import 'reflect-metadata';

import '@desygner/ui-data-access-user';
import { IUserService, TYPES as USER_SERVICE_TYPES } from '@desygner/ui-data-access-user';
import {
  TYPES as PROJECT_SERVICE_TYPES,
  ProjectData,
  ProjectStoreInjector,
} from '@desygner/ui-features-projects';
import {
  TYPES as RESTRICTIONS_TYPES,
  RestrictionsFacade,
} from '@desygner/ui-features-restrictions';

import { TYPES } from '../../inversify.types';
import { IConfigStoreInjector, IUserStoreInjector, IViewerStoreInjector } from '../../store';
import { ProjectAction } from '../../store/viewer/viewer.types';
import { IPostMessageManager } from './postmessage-manager';
import { IWorkflowManager } from './workflow-manager';

export interface IRestrictionsManager {
  init: () => void;
}

@injectable()
export class RestrictionsManager implements IRestrictionsManager {
  private projectSource: string | null = null;

  constructor(
    @inject(TYPES.Store.Viewer)
    private readonly viewerStore: IViewerStoreInjector,

    @inject(PROJECT_SERVICE_TYPES.ProjectsStore)
    private readonly projectsStore: ProjectStoreInjector,

    @inject(TYPES.Store.Config)
    private readonly configStore: IConfigStoreInjector,

    @inject(RESTRICTIONS_TYPES.RestrictionsFacade)
    private readonly restrictionsFacade: RestrictionsFacade,

    @inject(USER_SERVICE_TYPES.UserService)
    private readonly userService: IUserService,

    @inject(TYPES.Store.User)
    private readonly userStore: IUserStoreInjector,

    @inject(TYPES.Store.Viewer)
    private readonly projectStore: IViewerStoreInjector,

    @inject(TYPES.Viewer.PostMessageManager)
    private readonly postMessageManager: IPostMessageManager,

    @inject(TYPES.Viewer.WorkflowManager)
    private readonly workflowManager: IWorkflowManager
  ) {}

  public async init() {
    try {
      this.projectSource = new URLSearchParams(window.location.search).get('source');

      await this.loadPermissions();
      const isProjectEditable = this.canEditProject();
      const isProjectUsable = this.canUseProject();
      const isProjectSharable = this.canShareProject();
      const requireApproval = this.getProjectRequireApproval();

      this.viewerStore.getState().setIsProjectEditable(isProjectEditable);
      this.viewerStore.getState().setIsProjectUsable(isProjectUsable);
      this.viewerStore.getState().setIsProjectSharable(isProjectSharable);
      this.viewerStore.getState().setIsProjectActionRequireApproval(requireApproval);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  }

  private async loadPermissions() {
    const permissionsPromises = [this.getTemplatePermissions(), this.getUserMembership()];

    return Promise.all(permissionsPromises).then(([, userMembership]) => {
      this.userStore.getState().setMembership(userMembership);
    });
  }

  public async getTemplatePermissions() {
    const project = this.projectsStore.getState().project.currentProject;
    const templateId = project && 'template' in project && project.template;

    if (!templateId) return null;

    await this.restrictionsFacade.loadTemplatePermissions(templateId);

    return this.restrictionsFacade.templateRestriction;
  }

  private projectType() {
    return this.viewerStore.getState().project.type;
  }

  private isDesignFromTemplate() {
    const project = this.projectsStore.getState().project.currentProject;
    const template = project && 'template' in project && project.template;
    const isDesign = this.viewerStore.getState().project.type === 'design';

    return !!template && isDesign && this.isWorkspace();
  }

  public async getUserMembership() {
    const membership = await this.userService.getMembership();

    return membership.data;
  }

  private isWorkspace() {
    return this.viewerStore.getState().company?.is_workspace ?? false;
  }

  private isAdmin() {
    return this.userStore.getState().membership?.is_admin ?? false;
  }

  private isDraft() {
    const currentProject = this.projectsStore.getState().project.currentProject;

    return currentProject && 'is_draft' in currentProject && currentProject.is_draft;
  }

  private isOwner() {
    return this.viewerStore.getState().project.isOwner === true;
  }

  private isCurrentUserCollaborator() {
    const userId = this.userStore.getState().userId;

    return this.viewerStore
      .getState()
      .collaborators.some((collaborator) => collaborator.permissionUserId === userId);
  }

  private restrictGuestUser() {
    const restrictGuestUser = this.configStore.getState().feature?.restrictGuestUser;
    const userId = this.userStore.getState().userId;

    return restrictGuestUser && !this.isWorkspace() && !userId;
  }

  private isFromFeed() {
    return this.projectSource === 'feed';
  }

  private canEditProject() {
    if (this.isFromFeed() || this.restrictGuestUser()) return false;

    switch (this.projectType()) {
      case 'design': {
        return this.isOwner() || this.isCurrentUserCollaborator();
      }
      case 'template':
        return this.isWorkspace() && this.isAdmin();
      default:
        return false;
    }
  }

  private canUseProject() {
    if (this.isFromFeed() || this.restrictGuestUser()) return false;

    if (this.projectType() === 'template') {
      return this.isWorkspace() && !this.isDraft();
    }

    return false;
  }

  private navigateToEditor(url: string): void {
    const openInNewTab = this.configStore.getState().feature?.openEditorInNewTab ?? true;

    if (openInNewTab) {
      window.open(url, '_blank');
    } else if (this.postMessageManager.isEmbedded()) {
      this.postMessageManager.sendToParent({
        type: 'NAVIGATE',
        payload: { url },
      });
    } else {
      window.location.href = url;
    }
  }

  public navigateToProjectEditor() {
    const { project } = this.projectStore.getState();
    const { credentials } = this.userStore.getState();
    const deepLinkBaseUrl = this.configStore.getState().resources.deepLinkUrl;
    const openInNewTab = this.configStore.getState().feature?.openEditorInNewTab ?? true;

    const referrer = `create/${project.type}`;
    const brandName = this.configStore.getState().brand.name;
    const redirectUrl = new URL(deepLinkBaseUrl);

    redirectUrl.pathname = `/${brandName.toLowerCase()}-editor`;
    redirectUrl.searchParams.set('user_project_id', project.id);
    redirectUrl.searchParams.set('user_token', credentials.token);
    redirectUrl.searchParams.set('user_hash', credentials.hash);
    redirectUrl.searchParams.set('referrer', referrer);

    this.navigateToEditor(redirectUrl.toString());
  }

  public async createProjectAndNavigateToEditor(
    createProjectAsync: () => Promise<ProjectData> | undefined
  ) {
    const { credentials } = this.userStore.getState();
    const deepLinkBaseUrl = this.configStore.getState().resources.deepLinkUrl;
    const projectType = this.projectStore.getState().project.type;
    const openInNewTab = this.configStore.getState().feature?.openEditorInNewTab ?? true;

    const referrer = `create/${projectType}`;
    const brandName = this.configStore.getState().brand.name;
    const redirectUrl = new URL(deepLinkBaseUrl);

    redirectUrl.pathname = `/${brandName.toLowerCase()}-editor`;
    redirectUrl.searchParams.set('user_token', credentials.token);
    redirectUrl.searchParams.set('user_hash', credentials.hash);
    redirectUrl.searchParams.set('referrer', referrer);

    const newProject = await createProjectAsync();

    if (!newProject) return;

    redirectUrl.searchParams.set('user_project_id', newProject.encoded_id);
    redirectUrl.searchParams.set('referrer', 'create/design');

    this.navigateToEditor(redirectUrl.toString());
  }

  private canShareProject() {
    if (this.isFromFeed() || this.restrictGuestUser()) return false;

    const isTemplate = this.projectType() === 'template';

    if (isTemplate) return true;

    const canShareByPermission = this.getProjectSharePermission();

    return canShareByPermission && (this.isOwner() || this.isCurrentUserCollaborator());
  }

  private getProjectSharePermission() {
    const { templateRestriction } = this.restrictionsFacade;

    if (!this.isDesignFromTemplate()) return true;

    const canShareByPermission =
      templateRestriction?.function_share_file ||
      (templateRestriction?.function_request_share_file && this.workflowManager.isEnabled());

    return Boolean(canShareByPermission);
  }

  private getProjectRequireApproval() {
    const { templateRestriction } = this.restrictionsFacade;

    if (!this.isDesignFromTemplate() || !this.workflowManager.isEnabled())
      return Object.values(ProjectAction).reduce<Record<ProjectAction, boolean>>((acc, value) => {
        acc[value] = false;

        return acc;
      }, {} as Record<ProjectAction, boolean>);

    const requireApprovalByPermission = {
      [ProjectAction.Share]: templateRestriction?.function_request_share_file,
      [ProjectAction.Download]: templateRestriction?.function_request_download_file,
    };

    return requireApprovalByPermission;
  }
}
